/**
 * Mapping graph — pure, verbatim from source nodeDefs (component.js:728-802).
 *
 * Note: the Builder UI renders the `fields` array as a table; nodes/edges are
 * computed for resolution substitution + issue counts. Geometry is preserved
 * exactly (W map, columns cx16/tx380/vx712, y offsets) for fidelity.
 */
import type { Direction, GraphEdge, GraphNode, MappingField } from './types';

export type Resolutions = { subject: string | null; name: string | null };

const W: Record<GraphNode['kind'], number> = { canonical: 196, transform: 152, vendor: 196 };

type NodeDefs = { nodes: GraphNode[]; edges: GraphEdge[]; fields: MappingField[] };

/** field id used to key overrides/expansion: dir + ':' + (node | sanitized from). */
export function fieldId(dir: Direction, f: MappingField): string {
  return dir + ':' + (f.node || String(f.from).replace(/[^a-z0-9]+/gi, '_'));
}

/** Build the canonical↔transform↔vendor graph + field rows for a direction. */
export function nodeDefs(dir: Direction, vendor: string, R: Resolutions): NodeDefs {
  const V = vendor || 'ezyVet';
  const mk = (
    id: string,
    kind: GraphNode['kind'],
    x: number,
    y: number,
    label: string,
    sub: string,
    disc?: string | null,
    conf?: number | null,
  ): GraphNode => ({
    id,
    kind,
    x,
    y,
    w: W[kind],
    label,
    sub,
    disc: disc || null,
    conf: conf != null ? conf : null,
  });

  let nodes: GraphNode[];
  let edges: GraphEdge[];
  let fields: MappingField[];

  if (dir === 'outbound') {
    const cx = 16,
      tx = 380,
      vx = 712;
    nodes = [
      mk('c_party', 'canonical', cx, 28, 'party_ref', 'Engagement · ref'),
      mk('c_subject', 'canonical', cx, 110, 'subject_ref', 'Subject · {type,ref}'),
      mk('c_start', 'canonical', cx, 206, 'start_at', 'Engagement · timestamptz'),
      mk('c_end', 'canonical', cx, 276, 'end_at', 'Engagement · timestamptz'),
      mk('c_loc', 'canonical', cx, 372, 'location', 'Engagement · text'),
      mk('c_notes', 'canonical', cx, 442, 'notes', 'Engagement · text'),
      mk('t_coerce', 'transform', tx, 28, 'type-coerce', 'ref → id', null, 0.99),
      mk('t_enum', 'transform', tx, 110, 'enum-remap', 'Subject.type', 'subject', 0.61),
      mk('t_fmt', 'transform', tx, 206, 'format', 'ISO-8601', null, 0.97),
      mk('t_join', 'transform', tx, 290, 'join · N→1', 'duration Δ', null, 0.95),
      mk('v_contact', 'vendor', vx, 28, 'consult.contact_id', V),
      mk('v_animal', 'vendor', vx, 110, 'consult.animal_id', V, 'subject'),
      mk('v_start', 'vendor', vx, 206, 'consult.start_time', V + ' · ISO'),
      mk('v_duration', 'vendor', vx, 290, 'consult.duration_minutes', V + ' · int'),
      mk('v_resource', 'vendor', vx, 372, 'consult.resource', V),
      mk('v_desc', 'vendor', vx, 442, 'consult.description', V),
    ];
    edges = [
      ['c_party', 't_coerce', 0.99],
      ['t_coerce', 'v_contact', 0.99],
      ['c_subject', 't_enum', 0.61, 'subject'],
      ['t_enum', 'v_animal', 0.61, 'subject'],
      ['c_start', 't_fmt', 0.97],
      ['t_fmt', 'v_start', 0.97],
      ['c_start', 't_join', 0.95],
      ['c_end', 't_join', 0.95],
      ['t_join', 'v_duration', 0.95],
      ['c_loc', 'v_resource', 0.88],
      ['c_notes', 'v_desc', 0.92],
    ];
    fields = [
      { from: 'party_ref', fromType: 'Engagement · ref', to: 'consult.contact_id', toType: 'int', via: 'type-coerce', conf: '99%', state: 'settled' },
      { from: 'subject_ref', fromType: 'Subject · {type,ref}', to: 'consult.animal_id', toType: 'int', via: 'enum-remap', conf: '61%', state: 'needs', disc: 'subject', node: 't_enum' },
      { from: 'start_at', fromType: 'timestamptz', to: 'consult.start_time', toType: 'ISO-8601', via: 'format', conf: '97%', state: 'settled' },
      { from: 'start_at + end_at', fromType: 'timestamptz ×2', to: 'consult.duration_minutes', toType: 'int (min)', via: 'join · N→1', conf: '95%', state: 'settled' },
      { from: 'location', fromType: 'text', to: 'consult.resource', toType: 'string', via: 'default', conf: '88%', state: 'settled' },
      { from: 'notes', fromType: 'text', to: 'consult.description', toType: 'string', via: 'passthrough', conf: '92%', state: 'settled' },
    ];
  } else {
    const cx = 16,
      tx = 380,
      vx = 712;
    nodes = [
      mk('ic_ref', 'canonical', cx, 40, 'external_ref', 'Engagement · ref'),
      mk('ic_status', 'canonical', cx, 150, 'status', 'Engagement · enum'),
      mk('ic_start', 'canonical', cx, 260, 'start_at', 'Engagement · ts'),
      mk('ic_name', 'canonical', cx, 380, R.name ? R.name : '— unresolved —', R.name ? 'mapped' : 'Party? Contact?', 'name'),
      mk('it_coerce', 'transform', tx, 40, 'type-coerce', 'id → ref', null, 0.98),
      mk('it_enum', 'transform', tx, 150, 'enum-remap', 'status', null, 0.84),
      mk('it_coerce2', 'transform', tx, 260, 'type-coerce', 'ISO → ts', null, 0.96),
      mk('it_q', 'transform', tx, 380, 'resolve', 'ambiguous', 'name', 0.55),
      mk('iv_id', 'vendor', vx, 40, 'consult.id', V),
      mk('iv_status', 'vendor', vx, 150, 'consult.status', V + ' · enum'),
      mk('iv_start', 'vendor', vx, 260, 'consult.start_time', V + ' · ISO'),
      mk('iv_name', 'vendor', vx, 380, 'name', V + ' · string', 'name'),
    ];
    edges = [
      ['iv_id', 'it_coerce', 0.98],
      ['it_coerce', 'ic_ref', 0.98],
      ['iv_status', 'it_enum', 0.84],
      ['it_enum', 'ic_status', 0.84],
      ['iv_start', 'it_coerce2', 0.96],
      ['it_coerce2', 'ic_start', 0.96],
      ['iv_name', 'it_q', 0.55, 'name'],
      ['it_q', 'ic_name', 0.55, 'name'],
    ];
    fields = [
      { from: 'consult.id', fromType: 'int', to: 'external_ref', toType: 'Engagement · ref', via: 'type-coerce', conf: '98%', state: 'settled' },
      { from: 'consult.status', fromType: 'enum', to: 'status', toType: 'Engagement · enum', via: 'enum-remap', conf: '84%', state: 'settled' },
      { from: 'consult.start_time', fromType: 'ISO-8601', to: 'start_at', toType: 'timestamptz', via: 'type-coerce', conf: '96%', state: 'settled' },
      { from: 'name', fromType: 'string', to: R.name || '— unresolved —', toType: R.name ? 'mapped' : 'Party? Contact?', via: 'resolve', conf: '55%', state: R.name ? 'settled' : 'needs', disc: 'name', node: 'it_q' },
    ];
  }

  return { nodes, edges, fields };
}

/** Count unresolved discrepancies (subject + name). */
export function unresolvedCount(R: Resolutions): number {
  return (['subject', 'name'] as const).filter(d => !R[d]).length;
}
